import { useState } from 'react';
import { Camera, ImagePlus } from 'lucide-react';
import { ArtworkCamera } from '@/components/ArtworkCamera';
import { toast } from 'sonner';
import { api, formatErr, vLabel } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Btn } from '@/components/UI';
import { PrintingImage } from '@/components/CardTile';
import { CardPhotoEditor } from '@/components/CardPhotoEditor';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function CardImageManager({ card, variants, selectedVariant, onSaved }) {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('');
  const [file, setFile] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [camera, setCamera] = useState(false);
  if (user?.role !== 'admin') return null;
  const artwork = card.variant_images?.[scope || '__base__'];
  const resetFile = () => { setFile(null); setPrepared(null); setError(''); setConfirmRemove(false); setCamera(false); };
  const chooseFile = next => {
    resetFile();
    if (next?.size > 10 * 1024 * 1024) { setError('Images must be 10 MB or smaller'); return; }
    setFile(next || null);
  };
  const start = () => {
    const chosen = variants.some(variant => variant.type === selectedVariant) ? selectedVariant : '';
    setScope(chosen);
    resetFile(); setNote(card.variant_images?.[chosen || '__base__']?.note || ''); setOpen(true);
  };
  const save = async () => {
    if (!prepared || busy) return;
    setBusy(true); setError('');
    const body = new FormData();
    body.append('file', prepared); body.append('variant', scope); body.append('note', note);
    try {
      await api.put(`/admin/cards/${card.id}/image`, body);
      await onSaved();
      setOpen(false); setFile(null);
      toast.success('Card image saved. Ownership and storage are unchanged.');
    } catch (exception) { setError(formatErr(exception)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setError('');
    try {
      await api.delete(`/admin/cards/${card.id}/image`, { params: { variant: scope } });
      await onSaved();
      setConfirmRemove(false); setOpen(false); setFile(null);
      toast.success('Custom image removed. Catalogue artwork restored.');
    } catch (exception) { setError(formatErr(exception)); } finally { setBusy(false); }
  };
  return <>
    <Btn variant="ghost" className="mt-3" onClick={start} data-testid="manage-card-images"><ImagePlus className="h-4 w-4" />Manage card images · admin</Btn>
    <Dialog open={open} onOpenChange={value => { if (!busy) { setOpen(value); if (!value) resetFile(); } }}>
      <DialogContent className="max-h-[92dvh] max-w-4xl overflow-y-auto" data-testid="card-image-manager">
        <DialogHeader><DialogTitle>Manage artwork · {card.name}</DialogTitle><DialogDescription>Upload a card scan or photo, then crop and adjust its preview. It is shared catalogue artwork visible to everyone—not a private collection photo. No ownership or pocket assignments are changed.</DialogDescription></DialogHeader>
        <label className="text-sm font-bold">Image applies to<select aria-label="Image applies to" disabled={busy} value={scope} onChange={event => { const chosen = event.target.value; setScope(chosen); resetFile(); setNote(card.variant_images?.[chosen || '__base__']?.note || ''); }} className="mt-1 w-full rounded-xl border bg-white p-3">
          <option value="">Base artwork (fallback for all printings)</option>{variants.map(variant => <option key={variant.type} value={variant.type}>{vLabel(variant.type)}</option>)}
        </select></label>
        <p className="text-sm text-slate-500">For a Halloween or other stamped scan, select that exact printing. Base artwork never claims to show a missing stamp, and does not replace dedicated printing images.</p>
        {!file && <div className="mx-auto w-36"><PrintingImage card={card} variant={scope || undefined} q="high" /></div>}
        {artwork?.admin_override && <p className="text-sm text-emerald-800">An administrator-uploaded image is active for this selection.</p>}
        <Btn variant="ghost" disabled={busy} onClick={() => setCamera(value => !value)}><Camera className="h-4 w-4" />Take photo</Btn>
        {camera && <ArtworkCamera onClose={() => setCamera(false)} onPhoto={chooseFile} />}
        <label className="block text-sm font-bold">Phone camera / photo<input type="file" accept="image/*" capture="environment" disabled={busy} onChange={event => { chooseFile(event.target.files?.[0]); event.target.value = ''; }} className="mt-1 block w-full rounded-xl border p-3 text-sm" /></label>
        <label className="block text-sm font-bold">Card image (JPEG, PNG or WebP)<input key={scope} type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={event => { chooseFile(event.target.files?.[0]); event.target.value = ''; }} className="mt-1 block w-full rounded-xl border p-3 text-sm" /></label>
        {file && <CardPhotoEditor file={file} disabled={busy} onChange={setPrepared} />}
        <p className="text-xs text-slate-500">Maximum 10 MB and 20 megapixels. Images are resized, converted to WebP and stripped of photo metadata. Upload only artwork you have permission to share.</p>
        <label className="block text-sm font-bold">Source note (optional, visible to everyone)<input disabled={busy} value={note} maxLength={240} onChange={event => setNote(event.target.value)} className="mt-1 w-full rounded-xl border p-3 text-sm" placeholder="For example: scan of my card" /></label>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {confirmRemove && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm"><p>Remove this administrator image and restore the catalogue fallback? No cards will be removed.</p><div className="mt-2 flex gap-2"><Btn variant="ghost" disabled={busy} onClick={() => setConfirmRemove(false)}>Keep image</Btn><Btn disabled={busy} onClick={remove}>Confirm removal</Btn></div></div>}
        <DialogFooter>
          {artwork?.admin_override && <Btn variant="ghost" disabled={busy} onClick={() => setConfirmRemove(true)}>Remove custom image</Btn>}
          <Btn variant="ghost" disabled={busy} onClick={() => { setOpen(false); resetFile(); }}>Cancel</Btn>
          <Btn disabled={busy || !prepared} onClick={save}>{busy ? 'Saving…' : 'Save image'}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
